import get from 'hs-lodash/get';
import { capabilities } from 'navigation-components/capabilities';
import { sidebarNotificationsComponentDef } from 'navigation-components/config';
import { useCapability, useComponentProps, useInvokeFunction } from 'navigation-components/platform/hooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppSelector } from '../../../hooks/redux';
import { useInitial } from '../../../hooks/useInitial';
import useUpdateSidebarWidth from '../../../hooks/useUpdateSidebarWidth';
import { getNotificationsComponentLoadedState } from '../../../reducers/AppConfig';
import { queryLocalStorage } from '../../../utils/localstorage';
import { getUrlParamValue } from '../../../utils/urlParamHelpers';
import { broadcastNotificationMessage } from '../utils/Messages';
const NOTIFICATION_SIDEBAR_DEV = 'NOTIFICATION_SIDEBAR_DEV';
export const NOTIFICATION_SIDEBAR_CLOSE = 'notification-sidebar:close';
function isNotificationsMessage(val) {
  // input is parsed JSON so it doesn't mean it's an object!
  return get(val, 'name') === NOTIFICATION_SIDEBAR_CLOSE;
}
export function useNotifications() {
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const isNotificationsLoadedState = useAppSelector(getNotificationsComponentLoadedState);
  const loadingTimerRef = useRef(null);
  const loadingTimeout = 1000;
  const {
    invoke: invokeSidebar
  } = useInvokeFunction(sidebarNotificationsComponentDef);
  const setComponentProps = useComponentProps(sidebarNotificationsComponentDef);
  const {
    subscribe: iframeToggle
  } = useCapability(capabilities.TOGGLE_NOTIFICATION_SIDEBAR_CAP.getName());
  const hasOpenedFromUrl = useRef(false);
  useUpdateSidebarWidth(isNotificationsOpen, invokeSidebar);
  useEffect(() => {
    iframeToggle(args => {
      setIsNotificationsOpen(args.isOpen);
      if (!args.isOpen) {
        // Rest the view filters prop of the nav component
        setComponentProps({});
      }
    });
  }, [iframeToggle, setComponentProps]);
  useEffect(() => {
    if (isNotificationsOpen) {
      const handleResize = () => {
        void invokeSidebar('onScreenSizeChange', {
          width: window.innerWidth
        });
      };
      handleResize();
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isNotificationsOpen, invokeSidebar]);
  useEffect(() => {
    if (isNotificationsOpen) {
      //  We don't want to close the sidebar from Nav, only open it from here.
      void invokeSidebar('onSidebarOpen', {
        isOpen: isNotificationsOpen
      });
    }
  }, [isNotificationsOpen, invokeSidebar]);
  useEffect(() => {
    if (isNotificationsOpen && isNotificationsLoadedState) {
      broadcastNotificationMessage('notifications-menu-opened');
    }
  }, [isNotificationsLoadedState, isNotificationsOpen]);
  const iframeOrigin = useInitial(() => queryLocalStorage(NOTIFICATION_SIDEBAR_DEV) ? window.location.origin.replace('app', 'local') : window.location.origin);
  const handleMessage = useCallback(event => {
    if (event.origin !== iframeOrigin || typeof event.data !== 'string') {
      return;
    }
    let message;
    try {
      message = JSON.parse(event.data);
    } catch (e) {
      return;
    }
    if (isNotificationsMessage(message)) {
      setIsNotificationsOpen(false);
    }
  }, [iframeOrigin]);
  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [handleMessage]);
  useEffect(() => {
    // Should open automatically but if not rerun when notificationsLoadedState changes
    if (getUrlParamValue('openSidebar') === 'true' && !hasOpenedFromUrl.current) {
      setIsNotificationsOpen(true);
      hasOpenedFromUrl.current = true;
    }
  }, [isNotificationsLoadedState]);
  useEffect(() => {
    if (loadingTimerRef.current) {
      clearTimeout(loadingTimerRef.current);
    }
    loadingTimerRef.current = setTimeout(() => {
      setIsTransitioning(false);
    }, loadingTimeout);
    return () => {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
    };
  }, [isTransitioning]);
  const notificationsOnClick = useCallback(() => {
    if (!isTransitioning) {
      setIsNotificationsOpen(!isNotificationsOpen);
      setIsTransitioning(true);
    } else if (window.hubspot &&
    // @ts-expect-error - No notifications type in the hubspot window object
    window.hubspot.notifications &&
    // @ts-expect-error - No notifications type in the hubspot window object
    window.hubspot.notifications.openSidebar) {
      // @ts-expect-error - No notifications type in the hubspot window object
      window.hubspot.notifications.openSidebar();
      setIsNotificationsOpen(true);
    }
  }, [isTransitioning, isNotificationsOpen]);
  return [isNotificationsOpen, notificationsOnClick];
}